import { useQueryClient } from "@tanstack/react-query";

import { RefreshCwIcon, XIcon } from "~/lib/icons";
import { projectQueryKeys } from "~/lib/projectReactQuery";

import { DiffPanelLoadingState, DiffPanelShell, type DiffPanelMode } from "../DiffPanelShell";
import { Button } from "../ui/button";

import { FileTree } from "./FileTree";

export interface FilesPanelProps {
  readonly mode: DiffPanelMode;
  readonly cwd: string | null;
  readonly onClosePanel: () => void;
}

export function FilesPanel({ mode, cwd, onClosePanel }: FilesPanelProps) {
  const queryClient = useQueryClient();

  const header = (
    <div className="flex w-full items-center justify-between gap-2">
      <span className="truncate text-xs font-medium text-foreground">Files</span>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="size-7"
          disabled={!cwd}
          onClick={() => {
            // The server also caches directory listings for ~15s; there is no
            // endpoint to bust that cache, so this only forces the client to
            // refetch. It still surfaces newly created/deleted files once the
            // server-side TTL has naturally expired.
            void queryClient.invalidateQueries({ queryKey: projectQueryKeys.all });
          }}
        >
          <RefreshCwIcon className="size-3.5" />
          <span className="sr-only">Refresh files</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="size-7"
          onClick={onClosePanel}
        >
          <XIcon className="size-3.5" />
          <span className="sr-only">Close files panel</span>
        </Button>
      </div>
    </div>
  );

  if (!cwd) {
    return (
      <DiffPanelShell mode={mode} header={header}>
        <DiffPanelLoadingState label="No workspace available for this thread." />
      </DiffPanelShell>
    );
  }

  return (
    <DiffPanelShell mode={mode} header={header}>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <FileTree cwd={cwd} />
      </div>
    </DiffPanelShell>
  );
}
